import React, { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AuthImage } from "@/components/AuthImage";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

/* Batch 33 — supporting evidence pictures for MANUAL punches.
   Supervisors/admins attach proof (e.g. a WhatsApp screenshot showing the
   person was at work) to a manually added clock-in/out. Unlimited pictures,
   stored in GridFS and served only via the authenticated /api/media endpoint.
   Used on the Manual punches page and inside PunchCard for manual entries. */
export const EvidenceManager = ({ punch, compact = false }) => {
  const { t } = useLang();
  const queryClient = useQueryClient();
  const fileRef = useRef(null);
  const [viewerIdx, setViewerIdx] = useState(null); // null = closed
  const [deleteId, setDeleteId] = useState(null);

  const docIds = punch.supportingDocIds || [];

  const uploadMutation = useMutation({
    mutationFn: async (files) => {
      const fd = new FormData();
      files.forEach((f) => fd.append("documents", f));
      return (await api.post(`/punches/${punch.id}/documents`, fd)).data;
    },
    onSuccess: () => {
      toast.success(t("evidenceAdded"));
      queryClient.invalidateQueries();
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (docId) =>
      (await api.delete(`/punches/${punch.id}/documents/${docId}`)).data,
    onSuccess: () => {
      toast.success(t("evidenceDeleted"));
      setDeleteId(null);
      setViewerIdx(null);
      queryClient.invalidateQueries();
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Error"),
  });

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file later
    if (files.length > 0) uploadMutation.mutate(files);
  };

  const thumbSize = compact ? "h-9 w-9" : "h-12 w-12";

  return (
    <div data-testid="evidence-manager" className="flex flex-wrap items-center gap-1.5">
      {docIds.map((docId, i) => (
        <button
          key={docId}
          type="button"
          data-testid="evidence-thumbnail"
          title={t("viewEvidence")}
          onClick={() => setViewerIdx(i)}
          className={`overflow-hidden rounded-md border border-border transition-opacity duration-150 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${thumbSize}`}
        >
          <AuthImage photoId={docId} thumb className="h-full w-full" alt="evidence" />
        </button>
      ))}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        data-testid="evidence-file-input"
        onChange={onPick}
      />
      <Button
        size="sm"
        variant="outline"
        data-testid="add-evidence-button"
        disabled={uploadMutation.isPending}
        onClick={() => fileRef.current?.click()}
        className={compact ? "h-8 px-2 text-xs" : "h-8"}
      >
        {uploadMutation.isPending
          ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> {t("uploading")}</>
          : <><ImagePlus className="mr-1 h-3.5 w-3.5" /> {t("addEvidence")}</>}
      </Button>

      {/* ---- full-size viewer with prev / next ---- */}
      <Dialog open={viewerIdx !== null} onOpenChange={(v) => !v && setViewerIdx(null)}>
        <DialogContent
          data-testid="evidence-viewer"
          aria-describedby={undefined}
          className="flex h-[100dvh] w-screen max-w-none flex-col gap-0 rounded-none border-0 bg-black/95 p-0 text-white backdrop-blur sm:rounded-none"
        >
          <div className="flex shrink-0 items-center gap-3 px-4 py-3 pr-12">
            <div className="min-w-0">
              <DialogTitle className="truncate text-sm font-semibold text-white">
                {t("evidence")} — {punch.userName}
              </DialogTitle>
              <p data-testid="evidence-viewer-counter"
                className="font-mono text-xs tabular-nums text-white/70">
                {viewerIdx !== null ? viewerIdx + 1 : 0} / {docIds.length}
              </p>
            </div>
            <Button
              size="sm" variant="ghost"
              data-testid="evidence-delete-button"
              disabled={deleteMutation.isPending}
              onClick={() => setDeleteId(docIds[viewerIdx])}
              className="ml-auto h-8 text-white/80 hover:bg-white/15 hover:text-white"
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" /> {t("delete")}
            </Button>
          </div>

          <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-auto p-3">
            {docIds.length > 1 && (
              <Button
                size="icon" variant="ghost"
                data-testid="evidence-prev-button"
                aria-label={t("back")}
                onClick={() => setViewerIdx((i) => (i - 1 + docIds.length) % docIds.length)}
                className="absolute left-2 top-1/2 z-10 h-10 w-10 -translate-y-1/2 rounded-full bg-black/40 text-white hover:bg-white/20 hover:text-white"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            {viewerIdx !== null && docIds[viewerIdx] && (
              <AuthImage
                key={docIds[viewerIdx]}
                photoId={docIds[viewerIdx]}
                thumb={false}
                testId="evidence-full-image"
                className="max-h-[calc(100dvh-110px)] w-auto max-w-full rounded-lg object-contain"
                alt="evidence full size"
              />
            )}
            {docIds.length > 1 && (
              <Button
                size="icon" variant="ghost"
                data-testid="evidence-next-button"
                aria-label={t("next")}
                onClick={() => setViewerIdx((i) => (i + 1) % docIds.length)}
                className="absolute right-2 top-1/2 z-10 h-10 w-10 -translate-y-1/2 rounded-full bg-black/40 text-white hover:bg-white/20 hover:text-white"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ---- delete confirmation ---- */}
      <AlertDialog open={deleteId !== null} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent data-testid="evidence-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("deleteEvidenceTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("deleteEvidenceBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="evidence-delete-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="evidence-delete-confirm"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
